import { useMemo } from 'react';

import { useFlags } from 'providers/FlagsProvider';

import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { resolveGymSteps } from './utils';

/**
 * Hook that resolves the gym signup flow's steps.
 *
 * The step map is derived from feature flags — currently whether
 * `partners.acquisition.bypass-activities` is on, which drops the
 * `activities` step from the sequence.
 *
 * @returns Object containing the resolved step map:
 *
 * - `steps` - Current gym flow steps
 *
 * @example
 * ```tsx
 * const { steps } = useSignupSteps();
 * ```
 */
export function useSignupSteps() {
  const flags = useFlags();

  const isBypassActivitiesExperiment = useFeatureIsOn(
    'partners.acquisition.bypass-activities',
  );

  const signupSteps = useMemo(() => {
    const steps = resolveGymSteps(flags, isBypassActivitiesExperiment);

    return {
      steps,
    };
  }, [flags, isBypassActivitiesExperiment]);

  return signupSteps;
}

export type SignupSteps = ReturnType<typeof useSignupSteps>;

export type Steps = SignupSteps[keyof SignupSteps];
